import { groupBy, map, sumBy } from "lodash";
import { PriceGroupModel, PriceListResponse } from "../mvcapi/models/buyticket";
import { AddToCartPackageRequest, AddToCartRequest, AddToCartRequestBase, PriceList, AddToCartTicketPriceRequest } from "../mvcapi/models/buyticket/ticketselection";
import { PackageInfoModel } from "../mvcapi/models/common/package-info-model";
import { addItemToCartTrackingEvent } from "../types/models";
import { trackEcommerceEvent } from "../../js/client-tracking";

export function trackItemView(integ, eventDateInfo) {
    const trackingData = {
        type: 'itemViewed',
        currency: eventDateInfo.CurrencyCode,
        total: 0, // We could provide this someday for better reporting. We could find a default price somewhere. Ex.: How much money you lost with people abandonning their purchase.
        items: [
            {
                name: eventDateInfo.ProductNameFr,
                code: eventDateInfo.EventDateCode,
                producerName: eventDateInfo.ProducerName,
                category: eventDateInfo.CategoryFr // I hardcode FR here because we want it to be easy to group in analytics reports.
            }
        ]
    };

    return trackEcommerceEvent(integ, trackingData);
}

/**
 * Prepare tracking data for addTicketsToCart event
 * @param currency Currency of the Event/POS
 * @param addToCartRequest Object containing the tickets with their prices
 * @param eventDateName The name of the event date
 * @param eventDateCategory The category of the event date
 * @param eventDateCode The code of the event date
 * @param priceGroupsCache The complete price list for the event
 * @returns The object containing the tracking data
 */
export function prepareNewAddTicketsToCartTrackingData(currency: string, addToCartRequest: AddToCartRequestBase, eventDateName: string, eventDateCategory: string, eventDateCode: string, priceGroupsCache: Map<number, PriceGroupModel>): addItemToCartTrackingEvent {
    const trackingData: addItemToCartTrackingEvent = {
        type: 'addItemToCart',
        currency: currency,
        total: 0, // This one doesn't work : addToCartRequest.total so we calculate it at the end
        items: []
    };

    let ticketPrices: AddToCartTicketPriceRequest[] = null;

    if ((addToCartRequest as AddToCartPackageRequest).packageID > 0) { // If Package
        throw new Error("Use prepareAddPackageToCartTrackingData instead for package type."); // TODO: We could mix both function together to make it simple from the caller point of view.
    } else // Else, normal event
        ticketPrices = (addToCartRequest as AddToCartRequest).ticketPrices;

    const priceInfoById = groupBy(ticketPrices, ({priceID}) => priceID);
    trackingData.items = Object.entries(priceInfoById).map(([id, priceGroups]) => {
        const priceId = parseInt(id);
        const priceGroup = priceGroupsCache ? priceGroupsCache.get(priceId) : null;

        return {
            name: eventDateName,
            code: eventDateCode,
            category: eventDateCategory,
            quantity: priceGroups.length,
            price: priceGroup ? priceGroup.Total : 0,
        };
    });
    trackingData.total = sumBy(trackingData.items, (item) => item.price * item.quantity);

    return trackingData;
}

/**
 * Prepare tracking data for addTicketsToCart event in the context of a package
 * @deprecated This is the old way to track the event. It is only used in ticketpriceselection for package. Use {@link prepareNewAddPackageToCartTrackingData} instead.
 */
export function prepareNewAddPackageToCartTrackingData(currency: string, packageInfo: PackageInfoModel, addToCartRequest: AddToCartPackageRequest, priceList: PriceListResponse, priceGroupsCache: Map<number, PriceGroupModel>): addItemToCartTrackingEvent {

    // Prendre le nombre de forfait acheté et non pas le nombre de billet
    const numberOfPackage = sumBy(addToCartRequest.eventDates[0].ticketPrices, (price) => price.ticketQuantity);

    // We cannot use this because they are not correct in a package : const totalMoney = sumBy(addToCartRequest.eventDates, (edReq) => edReq.total);
    const singleEventsTracking = map(addToCartRequest.eventDates, (edReq) => prepareNewAddTicketsToCartTrackingData(currency, edReq, "", "", "", priceGroupsCache));
    const totalMoney = sumBy(singleEventsTracking, (edTracking) => sumBy(edTracking.items, (item) => item.price * item.quantity));
    // Prendre le total d'argent

    const approxUnitPrice = totalMoney / numberOfPackage;

    //console.log(`numberOfPackage: ${numberOfPackage}, totalMoney: ${totalMoney}, approxUnitPrice: ${approxUnitPrice}`);

    // Faire 1 item avec qty de forfait et total/qté de forfait
    const trackingData: addItemToCartTrackingEvent = {
        type: 'addItemToCart',
        currency: currency,
        total: totalMoney,
        items: [ { 
            name: packageInfo.Name,
            code: packageInfo.PackageID.toString(),
            category: "Forfait",
            quantity: numberOfPackage,
            price: approxUnitPrice
        }]
    };

    return trackingData;
}








/**
 * Prepare tracking data for addTicketsToCart event
 * @deprecated This is the old way to track the event. It is only used in ticketpriceselection for package. Use {@link prepareNewAddTicketsToCartTrackingData} instead.
 * @param currency Currency of the Event/POS
 * @param addToCartRequest Object containing the tickets with their prices
 * @param priceList All the price list for the event
 * @returns The object containing the tracking data
 */
export function prepareAddTicketsToCartTrackingData(currency: string, addToCartRequest: AddToCartRequest, priceList: PriceList): addItemToCartTrackingEvent {
    const trackingData: addItemToCartTrackingEvent = {
        type: 'addItemToCart',
        currency: currency,
        total: 0, // This one doesn't work : addToCartRequest.total so we calculate it at the end
        items: []
    };

    // get event date info with id

    addToCartRequest.ticketPrices.forEach((price) => {
        const ed = priceList.EventDates.find((ed) => ed.EventDateID == addToCartRequest.eventDateID);
        const pg = ed.PriceGroups.find((pg) => pg.PriceID == price.priceID);
        trackingData.items.push({
            name: ed.EventDateNameFr,
            code: ed.EventDateCode,
            category: ed.EventCategoryFr,
            quantity: price.ticketQuantity, // group by price.priceID
            price: pg.Total
        });
    });

    trackingData.total = sumBy(trackingData.items, (item) => item.price * item.quantity);

    return trackingData;
}

/**
 * Prepare tracking data for addTicketsToCart event in the context of a package
 * @deprecated This is the old way to track the event. It is only used in ticketpriceselection for package. Use {@link prepareNewAddPackageToCartTrackingData} instead.
 */
export function prepareAddPackageToCartTrackingData(currency: string, packageInfo: PackageInfoModel, addToCartRequest: AddToCartPackageRequest, priceList: PriceListResponse): addItemToCartTrackingEvent {

    // Prendre le nombre de forfait acheté et non pas le nombre de billet
    const numberOfPackage = sumBy(addToCartRequest.eventDates[0].ticketPrices, (price) => price.ticketQuantity);

    // We cannot use this because they are not correct in a package : const totalMoney = sumBy(addToCartRequest.eventDates, (edReq) => edReq.total);
    const singleEventsTracking = map(addToCartRequest.eventDates, (edReq) => prepareAddTicketsToCartTrackingData(currency, edReq, priceList));
    const totalMoney = sumBy(singleEventsTracking, (edTracking) => sumBy(edTracking.items, (item) => item.price * item.quantity));
    // Prendre le total d'argent

    const approxUnitPrice = totalMoney / numberOfPackage;

    //console.log(`numberOfPackage: ${numberOfPackage}, totalMoney: ${totalMoney}, approxUnitPrice: ${approxUnitPrice}`);

    // Faire 1 item avec qty de forfait et total/qté de forfait
    const trackingData: addItemToCartTrackingEvent = {
        type: 'addItemToCart',
        currency: currency,
        total: totalMoney,
        items: [ { 
            name: packageInfo.Name,
            code: packageInfo.PackageID.toString(),
            category: "Forfait",
            quantity: numberOfPackage,
            price: approxUnitPrice
        }]
    };

    return trackingData;
}